'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Star, BadgeCheck, GraduationCap } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '@/lib/trpc/provider';
import { cn } from '@/lib/utils';
import { CourseReviewForm } from './course-review-form';

export type PublicReviewItem = {
  id: string;
  rating: number;
  body: string;
  displayName: string;
  completed: boolean;
  createdAt: Date;
};

function StarRow({ value, className }: { value: number; className?: string }) {
  const rounded = Math.round(value);
  return (
    <div className={cn('flex gap-0.5', className)} aria-hidden>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={cn(
            'h-4 w-4',
            n <= rounded
              ? 'fill-eifo-orange text-eifo-orange'
              : 'text-gray-300'
          )}
        />
      ))}
    </div>
  );
}

export function CourseReviews({
  courseId,
  reviewAverage,
  reviewCount,
  initialReviews,
}: {
  courseId: string;
  reviewAverage: number | null;
  reviewCount: number;
  initialReviews: PublicReviewItem[];
}) {
  const t = useTranslations('public.enrollment.reviews');
  const tc = useTranslations('common');
  const trpc = useTRPC();
  const [limit, setLimit] = useState(10);

  const reviewsQuery = useQuery(
    trpc.courseReview.getForCourse.queryOptions(
      { courseId, limit, offset: 0 },
      // Seed the first page from SSR so the default view doesn't refetch on
      // mount; "Show more" (limit>10) fetches a fresh, larger page.
      limit === 10 ? { initialData: initialReviews } : undefined
    )
  );

  const reviews = reviewsQuery.data ?? initialReviews;
  const hasMore = reviewCount > reviews.length && limit < 50;

  return (
    <section className="space-y-6">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-semibold text-eifo-primary">{t('heading')}</h2>
        {reviewCount > 0 && reviewAverage !== null && (
          <div className="flex items-center gap-2">
            <StarRow value={reviewAverage} />
            <span className="text-sm text-muted-foreground">
              {reviewAverage.toFixed(1)} ({reviewCount})
            </span>
          </div>
        )}
      </div>

      {reviews.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('noReviews')}
        </p>
      ) : (
        <ul className="space-y-4">
          {reviews.map((r) => (
            <li
              key={r.id}
              className="rounded-xl border bg-white p-5 shadow-sm"
            >
              <div className="mb-2 flex items-center justify-between gap-3">
                <StarRow value={r.rating} />
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    {t('verifiedParent')}
                  </span>
                  {r.completed && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-[#2fa5fb]">
                      <GraduationCap className="h-3.5 w-3.5" />
                      {tc('completed')}
                    </span>
                  )}
                </div>
              </div>
              <p className="mb-2 text-sm text-gray-700">{r.body}</p>
              <p className="text-sm font-semibold text-eifo-primary">
                {r.displayName}
              </p>
            </li>
          ))}
        </ul>
      )}

      {hasMore && (
        <button
          type="button"
          onClick={() => setLimit((l) => Math.min(l + 10, 50))}
          className="text-sm font-medium text-eifo-orange hover:underline"
        >
          {t('showMore')}
        </button>
      )}

      <CourseReviewForm
        courseId={courseId}
        onSubmitted={() => reviewsQuery.refetch()}
      />
    </section>
  );
}
